import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, CheckCircle, XCircle, AlertCircle, Loader } from 'lucide-react';
import { PersonDetailData } from '../index';
import { asoService, ASORecord, CreateASORequest } from '../../../services/asoService';
import { cn } from '../../../utils/cn';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ASOTabProps {
  person: PersonDetailData;
  customerId: string;
  darkMode?: boolean;
}

const ASO_TYPE_LABELS: Record<string, string> = {
  admissional: 'Admissional',
  periodico: 'Periódico',
  retorno: 'Retorno ao Trabalho',
  demissional: 'Demissional',
};

const RESULT_LABELS: Record<string, string> = {
  apto: 'Apto',
  inapto: 'Inapto',
  apto_com_restricoes: 'Apto com Restrições',
};

const RESULT_COLORS: Record<string, string> = {
  apto: 'text-green-600 bg-green-50',
  inapto: 'text-red-600 bg-red-50',
  apto_com_restricoes: 'text-amber-600 bg-amber-50',
};

const emptyForm = (): CreateASORequest => ({
  aso_type: 'periodico',
  issue_date: '',
  expiry_date: '',
  result: 'apto',
  doctor_name: '',
  doctor_crm: '',
  notes: '',
});

export const ASOTab: React.FC<ASOTabProps> = ({ person, customerId, darkMode = false }) => {
  const [records, setRecords] = useState<ASORecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CreateASORequest>(emptyForm());
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchRecords = async () => {
    try {
      const data = await asoService.list(customerId, person.id);
      setRecords(data);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao carregar registros ASO');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, [person.id, customerId]);

  const latestValid = records.find(r => r.is_valid);
  const latestExpired = !latestValid && records.length > 0 ? records[0] : null;

  const handleOpenAdd = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const handleOpenEdit = (record: ASORecord) => {
    setEditingId(record.id);
    setForm({
      aso_type: record.aso_type,
      issue_date: record.issue_date.slice(0, 10),
      expiry_date: record.expiry_date.slice(0, 10),
      result: record.result,
      doctor_name: record.doctor_name ?? '',
      doctor_crm: record.doctor_crm ?? '',
      notes: record.notes ?? '',
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm());
  };

  const handleSave = async () => {
    if (!form.issue_date || !form.expiry_date) {
      toast.error('Preencha as datas de emissão e vencimento');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await asoService.update(customerId, person.id, editingId, form);
        toast.success('Registro ASO atualizado');
      } else {
        await asoService.create(customerId, person.id, form);
        toast.success('Registro ASO criado');
      }
      handleCancel();
      await fetchRecords();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao salvar registro ASO');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await asoService.delete(customerId, person.id, id);
      toast.success('Registro ASO excluído');
      setRecords(prev => prev.filter(r => r.id !== id));
    } catch (err: any) {
      toast.error(err.message || 'Erro ao excluir registro ASO');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return format(parseISO(dateStr), 'dd/MM/yyyy', { locale: ptBR });
    } catch {
      return dateStr.slice(0, 10);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {/* Current status */}
      <div className={cn('rounded-lg border p-3', darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50')}>
        <p className={cn('text-xs font-semibold uppercase tracking-wide mb-2', darkMode ? 'text-gray-400' : 'text-gray-500')}>
          Status atual
        </p>
        {records.length === 0 ? (
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>Sem ASO cadastrado</span>
          </div>
        ) : latestValid ? (
          <div className="flex items-center gap-2 text-green-600 text-sm font-medium">
            <CheckCircle className="w-4 h-4" />
            <span>Válido até {formatDate(latestValid.expiry_date)}</span>
            <span className={cn('ml-1 text-xs px-1.5 py-0.5 rounded', RESULT_COLORS[latestValid.result])}>
              {RESULT_LABELS[latestValid.result]}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-red-600 text-sm font-medium">
            <XCircle className="w-4 h-4" />
            <span>Vencido em {formatDate(latestExpired!.expiry_date)}</span>
          </div>
        )}
      </div>

      {/* Header + Add button */}
      <div className="flex items-center justify-between">
        <p className={cn('text-sm font-semibold', darkMode ? 'text-gray-200' : 'text-gray-800')}>
          Histórico
        </p>
        {!showForm && (
          <button
            onClick={handleOpenAdd}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Adicionar ASO
          </button>
        )}
      </div>

      {/* Inline form */}
      {showForm && (
        <div className={cn('rounded-lg border p-4 space-y-3', darkMode ? 'border-gray-600 bg-gray-750' : 'border-blue-200 bg-blue-50')}>
          <p className={cn('text-sm font-medium', darkMode ? 'text-gray-200' : 'text-gray-800')}>
            {editingId ? 'Editar ASO' : 'Novo ASO'}
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={cn('block text-xs font-medium mb-1', darkMode ? 'text-gray-300' : 'text-gray-700')}>
                Tipo *
              </label>
              <select
                value={form.aso_type}
                onChange={e => setForm(f => ({ ...f, aso_type: e.target.value as any }))}
                className={cn('w-full text-sm rounded-md border px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500',
                  darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-900'
                )}
              >
                {Object.entries(ASO_TYPE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={cn('block text-xs font-medium mb-1', darkMode ? 'text-gray-300' : 'text-gray-700')}>
                Resultado *
              </label>
              <select
                value={form.result}
                onChange={e => setForm(f => ({ ...f, result: e.target.value as any }))}
                className={cn('w-full text-sm rounded-md border px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500',
                  darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-900'
                )}
              >
                {Object.entries(RESULT_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={cn('block text-xs font-medium mb-1', darkMode ? 'text-gray-300' : 'text-gray-700')}>
                Data de Emissão *
              </label>
              <input
                type="date"
                value={form.issue_date}
                onChange={e => setForm(f => ({ ...f, issue_date: e.target.value }))}
                className={cn('w-full text-sm rounded-md border px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500',
                  darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-900'
                )}
              />
            </div>

            <div>
              <label className={cn('block text-xs font-medium mb-1', darkMode ? 'text-gray-300' : 'text-gray-700')}>
                Data de Vencimento *
              </label>
              <input
                type="date"
                value={form.expiry_date}
                onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))}
                className={cn('w-full text-sm rounded-md border px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500',
                  darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-900'
                )}
              />
            </div>

            <div>
              <label className={cn('block text-xs font-medium mb-1', darkMode ? 'text-gray-300' : 'text-gray-700')}>
                Médico Responsável
              </label>
              <input
                type="text"
                value={form.doctor_name ?? ''}
                onChange={e => setForm(f => ({ ...f, doctor_name: e.target.value }))}
                placeholder="Nome do médico"
                className={cn('w-full text-sm rounded-md border px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500',
                  darkMode ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                )}
              />
            </div>

            <div>
              <label className={cn('block text-xs font-medium mb-1', darkMode ? 'text-gray-300' : 'text-gray-700')}>
                CRM
              </label>
              <input
                type="text"
                value={form.doctor_crm ?? ''}
                onChange={e => setForm(f => ({ ...f, doctor_crm: e.target.value }))}
                placeholder="CRM 12345"
                className={cn('w-full text-sm rounded-md border px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500',
                  darkMode ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                )}
              />
            </div>
          </div>

          <div>
            <label className={cn('block text-xs font-medium mb-1', darkMode ? 'text-gray-300' : 'text-gray-700')}>
              Observações
            </label>
            <textarea
              value={form.notes ?? ''}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2}
              placeholder="Observações opcionais..."
              className={cn('w-full text-sm rounded-md border px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none',
                darkMode ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              )}
            />
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={handleCancel}
              className={cn('text-xs px-3 py-1.5 rounded-md border transition-colors',
                darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              )}
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 transition-colors"
            >
              {saving && <Loader className="w-3 h-3 animate-spin" />}
              {editingId ? 'Salvar alterações' : 'Adicionar'}
            </button>
          </div>
        </div>
      )}

      {/* Records list */}
      {records.length === 0 && !showForm ? (
        <p className={cn('text-sm text-center py-6', darkMode ? 'text-gray-500' : 'text-gray-400')}>
          Nenhum registro ASO cadastrado
        </p>
      ) : (
        <div className="space-y-2">
          {records.map(record => (
            <div
              key={record.id}
              className={cn('rounded-lg border p-3', darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white')}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={cn('text-sm font-medium', darkMode ? 'text-gray-200' : 'text-gray-800')}>
                      {ASO_TYPE_LABELS[record.aso_type] ?? record.aso_type}
                    </span>
                    <span className={cn('text-xs px-1.5 py-0.5 rounded font-medium', RESULT_COLORS[record.result])}>
                      {RESULT_LABELS[record.result] ?? record.result}
                    </span>
                    {record.is_valid ? (
                      <span className="text-xs text-green-600 flex items-center gap-0.5">
                        <CheckCircle className="w-3 h-3" /> Válido
                      </span>
                    ) : (
                      <span className="text-xs text-red-500 flex items-center gap-0.5">
                        <XCircle className="w-3 h-3" /> Vencido
                      </span>
                    )}
                  </div>
                  <p className={cn('text-xs mt-1', darkMode ? 'text-gray-400' : 'text-gray-500')}>
                    {formatDate(record.issue_date)} – {formatDate(record.expiry_date)}
                  </p>
                  {(record.doctor_name || record.doctor_crm) && (
                    <p className={cn('text-xs mt-0.5', darkMode ? 'text-gray-400' : 'text-gray-500')}>
                      {record.doctor_name}{record.doctor_crm ? ` · CRM ${record.doctor_crm}` : ''}
                    </p>
                  )}
                  {record.notes && (
                    <p className={cn('text-xs mt-1 italic', darkMode ? 'text-gray-500' : 'text-gray-400')}>
                      {record.notes}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleOpenEdit(record)}
                    className={cn('p-1.5 rounded hover:bg-gray-100 transition-colors', darkMode && 'hover:bg-gray-700')}
                    title="Editar"
                  >
                    <Pencil className={cn('w-3.5 h-3.5', darkMode ? 'text-gray-400' : 'text-gray-500')} />
                  </button>
                  <button
                    onClick={() => handleDelete(record.id)}
                    disabled={deletingId === record.id}
                    className={cn('p-1.5 rounded hover:bg-red-50 transition-colors', darkMode && 'hover:bg-red-900/20')}
                    title="Excluir"
                  >
                    {deletingId === record.id
                      ? <Loader className="w-3.5 h-3.5 animate-spin text-gray-400" />
                      : <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    }
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
